import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { ModelViewerElement } from '@google/model-viewer';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { getDeletedProducts, getProductsByUser } from '@/Api';
import { useStore } from '@/store/Store';
import type { LinkShareMode } from '@/types/Product.model';
import {
  convertUploadedModel,
  disposeObject3D,
  maxConversionWaitMs,
} from '@/utils/convertUploadedModel';
import {
  isValidProductName,
  productNameFromModelFilename,
  uniqueProductName,
} from '@/utils/productNameFromModelFilename';
import { tagsStringToArray } from '@/utils/tags';
import { createProductFromParts, metadataTableFromTags } from '@/utils/productSubmit';
import { getUserFriendlyErrorMessage } from '@/utils/errorHandling';
import { capturePreviewFromViewer, waitForViewerPaint } from '@/utils/productPreviewImage';
import type { Annotation } from '@/types/Annotation.model';
import type { ProductFormData } from '@/components/ProductForm/ProductForm';

export type ProductDraftStatus =
  'queued' | 'converting' | 'ready' | 'error' | 'creating' | 'created';

export type ProductDraft = {
  localId: string;
  file: File;
  productName: string;
  articleNumber: string;
  productPage: string;
  descriptionHtml: string;
  isPublic: boolean;
  category: string;
  tags: string;
  linkShareMode: LinkShareMode;
  linkSharePassword: string;
  metadata?: ProductFormData['metadata'];
  modelUrl: string;
  previewUri: string;
  nonGltfModel: THREE.Object3D | null;
  preparedGlb: Blob | null;
  cameraOrbit: string;
  cameraTarget: string;
  cameraFov: string;
  status: ProductDraftStatus;
  errorMessage?: string;
  createdProductId?: string;
  /** Model URL this preview was captured from — ignore preview if it does not match. */
  previewSourceModelUrl?: string;
  annotations: Annotation[];
};

export type BatchCreateResult = {
  created: { productId: string; productName: string }[];
  failed: { productName: string; error: string }[];
};

const PRODUCT_PAGE_RE =
  /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([\/\w.%+-]*)*\/?([?#][\w\-._~:\/?#[\]@!$&'()*+,;=%]*)?$/;

function newLocalId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function revokeIfBlob(url: string) {
  if (url.startsWith('blob:')) URL.revokeObjectURL(url);
}

function releaseDraftResources(draft: ProductDraft) {
  revokeIfBlob(draft.modelUrl);
  revokeIfBlob(draft.previewUri);
  disposeObject3D(draft.nonGltfModel);
}

function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

type SeedFromSingle = {
  file: File;
  productName?: string;
  articleNumber?: string;
  productPage?: string;
  descriptionHtml?: string;
  isPublic?: boolean;
  category?: string;
  tags?: string;
  linkShareMode?: LinkShareMode;
  linkSharePassword?: string;
  metadata?: ProductFormData['metadata'];
  modelUrl: string;
  previewUri: string;
  nonGltfModel: THREE.Object3D | null;
  preparedGlb?: Blob | null;
  cameraOrbit: string;
  cameraTarget: string;
  cameraFov: string;
  annotations?: Annotation[];
};

export function preparedGlbForSeed(file: File, preparedGlb: Blob | null | undefined): Blob | null {
  if (preparedGlb) return preparedGlb;
  return /\.glb$/i.test(file.name) ? file : null;
}

function cloneAnnotations(annotations: Annotation[] | undefined): Annotation[] {
  return (annotations ?? []).map((item) => ({ ...item }));
}

function tagsFromMetadata(metadata: ProductFormData['metadata'] | undefined): string {
  if (!metadata?.rows) return '';
  const rowKey = Object.keys(metadata.rows)[0];
  if (!rowKey) return '';
  return metadata.rows[rowKey]?.data?.Tags ?? '';
}

function emptyDraftForm(): Pick<
  ProductDraft,
  | 'articleNumber'
  | 'productPage'
  | 'descriptionHtml'
  | 'isPublic'
  | 'category'
  | 'tags'
  | 'linkShareMode'
  | 'linkSharePassword'
> {
  return {
    articleNumber: '',
    productPage: '',
    descriptionHtml: '',
    isPublic: true,
    category: '',
    tags: '',
    linkShareMode: 'open',
    linkSharePassword: '',
  };
}

export function draftToFormValues(draft: ProductDraft): ProductFormData {
  return {
    productName: draft.productName,
    productPage: draft.productPage,
    articleNumber: draft.articleNumber,
    isPublic: draft.isPublic,
    descriptionHtml: draft.descriptionHtml,
    linkShareMode: draft.isPublic ? 'open' : draft.linkShareMode,
    linkSharePassword: draft.linkSharePassword,
    updateLinkSharePassword: false,
    category: draft.category,
    metadata: draft.metadata ?? metadataTableFromTags(draft.tags),
  };
}

export function isDraftDetailsComplete(draft: ProductDraft): boolean {
  if (!isValidProductName(draft.productName)) return false;
  if (tagsStringToArray(draft.tags).length === 0) return false;
  if (draft.productPage && !PRODUCT_PAGE_RE.test(draft.productPage)) return false;
  if (draft.articleNumber.length > 200) return false;
  if (
    !draft.isPublic &&
    draft.linkShareMode === 'password' &&
    draft.linkSharePassword.trim().length < 4
  ) {
    return false;
  }
  return true;
}

/**
 * Create failed but conversion succeeded — model is still on the draft, so submit can retry.
 * Conversion failures stay `error` without `modelUrl` and need re-drop.
 */
export function isRetryableErrorDraft(draft: ProductDraft): boolean {
  return draft.status === 'error' && Boolean(draft.modelUrl);
}

/** Drafts still owed a create attempt (includes retryable errors). */
export function isPendingCreateDraft(draft: ProductDraft): boolean {
  if (draft.status === 'created') return false;
  if (draft.status === 'error') return Boolean(draft.modelUrl);
  return true;
}

/** Preview is valid only if it was captured from this draft's current model. */
export function draftPreviewUri(draft: ProductDraft): string {
  if (!draft.previewUri) return '';
  if (draft.modelUrl && draft.previewSourceModelUrl !== draft.modelUrl) {
    return '';
  }
  return draft.previewUri;
}

function draftValidationMessage(draft: ProductDraft, t: (key: string) => string): string | null {
  if (!isValidProductName(draft.productName)) return t('editProduct.invalidProductName');
  if (tagsStringToArray(draft.tags).length === 0) {
    return t('editProduct.tags.atLeastOneRequired');
  }
  if (draft.productPage && !PRODUCT_PAGE_RE.test(draft.productPage)) {
    return t('editProduct.invalidUrl');
  }
  if (draft.articleNumber.length > 200) return t('editProduct.articleNumberTooLong');
  if (
    !draft.isPublic &&
    draft.linkShareMode === 'password' &&
    draft.linkSharePassword.trim().length < 4
  ) {
    return t('editProduct.linkShare.passwordTooShort');
  }
  return null;
}

export function useProductBatchCreate(options: {
  enabled: boolean;
  modelViewerRef: RefObject<ModelViewerElement | null>;
  applyPreparedModel: (
    file: File | null,
    url: string,
    nonGltfModel: THREE.Object3D | null,
    preparedGlb?: Blob | null
  ) => void;
  onPreviewUriChange: (uri: string) => void;
  onCameraChange: (orbit: string, target: string, fov: string) => void;
  onRestoreAnnotations: (annotations: Annotation[]) => void;
}) {
  const { t } = useTranslation();
  const userId = useStore((s) => s.userProfile._id);
  const productSlots = useStore((s) => s.userPermissions.productSlots);

  const [drafts, setDrafts] = useState<ProductDraft[]>([]);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [usedSlots, setUsedSlots] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState<{ current: number; total: number } | null>(
    null
  );
  const [result, setResult] = useState<BatchCreateResult | null>(null);

  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const convertingLockRef = useRef(false);
  const submitCancelledRef = useRef(false);
  const [convertTick, setConvertTick] = useState(0);
  const activeDraftIdRef = useRef(activeDraftId);
  activeDraftIdRef.current = activeDraftId;

  const applyPreparedModelRef = useRef(options.applyPreparedModel);
  const onPreviewUriChangeRef = useRef(options.onPreviewUriChange);
  const onCameraChangeRef = useRef(options.onCameraChange);
  const onRestoreAnnotationsRef = useRef(options.onRestoreAnnotations);
  applyPreparedModelRef.current = options.applyPreparedModel;
  onPreviewUriChangeRef.current = options.onPreviewUriChange;
  onCameraChangeRef.current = options.onCameraChange;
  onRestoreAnnotationsRef.current = options.onRestoreAnnotations;

  const remainingSlots = usedSlots == null ? null : Math.max(0, (productSlots || 0) - usedSlots);

  const isBatchMode = drafts.length > 0;

  useEffect(() => {
    if (!options.enabled || !userId) return;
    // Server quota counts trash; live list alone under-counts used slots.
    Promise.all([getProductsByUser(userId), getDeletedProducts(userId)])
      .then(([live, deleted]) => setUsedSlots(live.length + deleted.length))
      .catch(() => setUsedSlots(null));
  }, [options.enabled, userId]);

  // Warn on tab close while drafts exist or a batch submit is in flight.
  useEffect(() => {
    if (!options.enabled) return;
    const shouldGuard = isSubmitting || drafts.length > 0;
    if (!shouldGuard) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [drafts.length, isSubmitting, options.enabled]);

  // Release blob URLs / GPU trees when the batch editor unmounts.
  useEffect(() => {
    return () => {
      submitCancelledRef.current = true;
      for (const draft of draftsRef.current) {
        releaseDraftResources(draft);
      }
    };
  }, []);

  const patchDraft = useCallback((localId: string, patch: Partial<ProductDraft>) => {
    setDrafts((prev) => {
      const next = prev.map((draft) =>
        draft.localId === localId ? { ...draft, ...patch } : draft
      );
      // Keep ref in sync for submit/validate loops that read between awaits.
      draftsRef.current = next;
      return next;
    });
  }, []);

  const applyDraftToViewer = useCallback((draft: ProductDraft) => {
    applyPreparedModelRef.current(
      draft.file,
      draft.modelUrl,
      draft.nonGltfModel,
      draft.preparedGlb
    );
    onPreviewUriChangeRef.current(draftPreviewUri(draft));
    onCameraChangeRef.current(draft.cameraOrbit, draft.cameraTarget, draft.cameraFov);
    onRestoreAnnotationsRef.current(cloneAnnotations(draft.annotations));
  }, []);

  const selectDraft = useCallback(
    (localId: string) => {
      const draft = draftsRef.current.find((item) => item.localId === localId);
      if (!draft) return;
      setActiveDraftId(localId);
      applyDraftToViewer(draft);
    },
    [applyDraftToViewer]
  );

  const makeDraftFromFile = useCallback(
    (file: File, takenNames: string[], fallbackIndex: number): ProductDraft => {
      const derived = productNameFromModelFilename(file.name);
      return {
        localId: newLocalId(),
        file,
        productName: uniqueProductName(derived, takenNames, fallbackIndex),
        ...emptyDraftForm(),
        modelUrl: '',
        previewUri: '',
        nonGltfModel: null,
        preparedGlb: null,
        cameraOrbit: '',
        cameraTarget: '',
        cameraFov: '',
        annotations: [],
        status: 'queued',
      };
    },
    []
  );

  const addFiles = useCallback(
    (files: File[], seed?: SeedFromSingle) => {
      if (!files.length && !seed) return;

      const existingKeys = new Set(draftsRef.current.map((d) => fileKey(d.file)));
      const incoming = files.filter((file) => {
        const key = fileKey(file);
        if (existingKeys.has(key)) return false;
        existingKeys.add(key);
        return true;
      });

      const skippedDupes = files.length - incoming.length;
      if (skippedDupes > 0) {
        notifications.show({
          title: t('editProduct.system'),
          message: t('editProduct.batch.duplicatesSkipped', { count: skippedDupes }),
        });
      }

      const room =
        remainingSlots == null
          ? incoming.length + (seed ? 1 : 0)
          : Math.max(0, remainingSlots - draftsRef.current.length);
      const wanted = incoming.length + (seed ? 1 : 0);
      if (room <= 0) {
        notifications.show({
          title: t('editProduct.system'),
          message: t('editProduct.batch.slotLimitReached'),
          color: 'red',
        });
        return;
      }
      if (wanted > room) {
        notifications.show({
          title: t('editProduct.system'),
          message: t('editProduct.batch.slotLimit', { count: room }),
          color: 'yellow',
        });
      }

      const taken = draftsRef.current.map((d) => d.productName);
      const next: ProductDraft[] = [];
      let slotsLeft = room;

      if (seed && slotsLeft > 0) {
        const name = uniqueProductName(
          seed.productName || productNameFromModelFilename(seed.file.name),
          taken,
          1
        );
        taken.push(name);
        next.push({
          localId: newLocalId(),
          file: seed.file,
          productName: name,
          ...emptyDraftForm(),
          articleNumber: seed.articleNumber ?? '',
          productPage: seed.productPage ?? '',
          descriptionHtml: seed.descriptionHtml ?? '',
          isPublic: seed.isPublic ?? true,
          category: seed.category ?? '',
          tags: seed.tags ?? tagsFromMetadata(seed.metadata),
          linkShareMode: seed.linkShareMode ?? 'open',
          linkSharePassword: seed.linkSharePassword ?? '',
          metadata: seed.metadata,
          modelUrl: seed.modelUrl,
          previewUri: seed.previewUri,
          previewSourceModelUrl: seed.previewUri && seed.modelUrl ? seed.modelUrl : undefined,
          nonGltfModel: seed.nonGltfModel,
          preparedGlb: preparedGlbForSeed(seed.file, seed.preparedGlb),
          cameraOrbit: seed.cameraOrbit,
          cameraTarget: seed.cameraTarget,
          cameraFov: seed.cameraFov,
          annotations: cloneAnnotations(seed.annotations),
          status: seed.modelUrl ? 'ready' : 'queued',
        });
        slotsLeft -= 1;
      }

      for (const file of incoming) {
        if (slotsLeft <= 0) break;
        const draft = makeDraftFromFile(file, taken, draftsRef.current.length + next.length + 1);
        taken.push(draft.productName);
        next.push(draft);
        slotsLeft -= 1;
      }

      if (next.length === 0) return;

      setDrafts((prev) => {
        const merged = [...prev, ...next];
        if (!activeDraftIdRef.current) {
          const firstReady = merged.find((d) => d.modelUrl) ?? merged[0];
          setActiveDraftId(firstReady.localId);
          if (firstReady.modelUrl) applyDraftToViewer(firstReady);
        }
        return merged;
      });
    },
    [applyDraftToViewer, makeDraftFromFile, remainingSlots, t]
  );

  const removeDraft = useCallback(
    (localId: string) => {
      const current = draftsRef.current;
      const target = current.find((d) => d.localId === localId);
      if (!target || target.status === 'creating') return;

      releaseDraftResources(target);

      const remaining = current.filter((d) => d.localId !== localId);
      draftsRef.current = remaining;
      setDrafts(remaining);

      if (activeDraftIdRef.current === localId) {
        const nextActive = remaining[0] ?? null;
        setActiveDraftId(nextActive?.localId ?? null);
        if (nextActive) {
          applyDraftToViewer(nextActive);
        } else {
          applyPreparedModelRef.current(null, '', null);
          onPreviewUriChangeRef.current('');
          onCameraChangeRef.current('', '', '');
          onRestoreAnnotationsRef.current([]);
        }
      }
    },
    [applyDraftToViewer]
  );

  const updateDraftFields = useCallback(
    (localId: string, patch: Partial<ProductDraft>) => {
      const current = draftsRef.current.find((d) => d.localId === localId);
      const clearRetry =
        current && isRetryableErrorDraft(current)
          ? { status: 'ready' as const, errorMessage: undefined }
          : null;
      patchDraft(localId, clearRetry ? { ...patch, ...clearRetry } : patch);
    },
    [patchDraft]
  );

  const syncFormValues = useCallback(
    (localId: string, values: ProductFormData) => {
      const current = draftsRef.current.find((d) => d.localId === localId);
      // Form edit clears a terminal create-error so Create all / stepper treat it as pending again.
      const clearRetry =
        current && isRetryableErrorDraft(current)
          ? { status: 'ready' as const, errorMessage: undefined }
          : {};
      patchDraft(localId, {
        productName: values.productName,
        articleNumber: values.articleNumber,
        productPage: values.productPage,
        descriptionHtml: values.descriptionHtml,
        isPublic: values.isPublic,
        category: values.category ?? '',
        tags: tagsFromMetadata(values.metadata),
        linkShareMode: values.isPublic ? 'open' : values.linkShareMode,
        linkSharePassword: values.linkSharePassword,
        metadata: values.metadata,
        ...clearRetry,
      });
    },
    [patchDraft]
  );

  /** Re-queue create failures that still have a converted model (no re-drop needed). */
  const retryFailedDrafts = useCallback(() => {
    for (const draft of draftsRef.current) {
      if (!isRetryableErrorDraft(draft)) continue;
      patchDraft(draft.localId, { status: 'ready', errorMessage: undefined });
    }
  }, [patchDraft]);

  const patchActiveDraft = useCallback(
    (patch: Partial<ProductDraft>) => {
      const id = activeDraftIdRef.current;
      if (!id) return;
      patchDraft(id, patch);
    },
    [patchDraft]
  );

  const commitOwnedPatch = useCallback(
    (localId: string, expectedModelUrl: string, patch: Partial<ProductDraft>) => {
      if (activeDraftIdRef.current !== localId) return;
      const draft = draftsRef.current.find((item) => item.localId === localId);
      if (!draft) return;
      if (expectedModelUrl && draft.modelUrl && draft.modelUrl !== expectedModelUrl) return;
      const nextPatch = { ...patch };
      if ('previewUri' in patch) {
        nextPatch.previewSourceModelUrl =
          patch.previewUri && expectedModelUrl ? expectedModelUrl : '';
      }
      patchDraft(localId, nextPatch);
    },
    [patchDraft]
  );

  const updateActivePreview = useCallback(
    (previewUri: string, cameraOrbit: string, cameraTarget: string, cameraFov: string) => {
      const id = activeDraftIdRef.current;
      if (!id) return;
      patchDraft(id, { previewUri, cameraOrbit, cameraTarget, cameraFov });
    },
    [patchDraft]
  );

  // Sequential conversion of queued drafts
  useEffect(() => {
    if (!options.enabled || convertingLockRef.current) return;
    const next = draftsRef.current.find((d) => d.status === 'queued');
    if (!next) return;

    convertingLockRef.current = true;
    patchDraft(next.localId, { status: 'converting', errorMessage: undefined });

    convertUploadedModel(next.file)
      .then((converted) => {
        const stillExists = draftsRef.current.some((d) => d.localId === next.localId);
        if (!stillExists) {
          // Removed while converting — do not resurrect into the viewer.
          revokeIfBlob(converted.modelUrl);
          disposeObject3D(converted.nonGltfModel);
          return;
        }

        // Keep GLB blob URL for the viewer; free the CAD Object3D tree.
        disposeObject3D(converted.nonGltfModel);

        patchDraft(next.localId, {
          status: 'ready',
          modelUrl: converted.modelUrl,
          nonGltfModel: null,
          preparedGlb: converted.preparedGlb,
          errorMessage: undefined,
        });
        if (activeDraftIdRef.current === next.localId || !activeDraftIdRef.current) {
          const latest = draftsRef.current.find((d) => d.localId === next.localId);
          const readyDraft: ProductDraft = {
            ...next,
            ...latest,
            status: 'ready',
            modelUrl: converted.modelUrl,
            nonGltfModel: null,
            preparedGlb: converted.preparedGlb,
            previewUri: latest?.previewUri || next.previewUri,
          };
          setActiveDraftId(next.localId);
          applyDraftToViewer(readyDraft);
        }
      })
      .catch((error: unknown) => {
        console.error('Batch model conversion failed:', error);
        if (!draftsRef.current.some((d) => d.localId === next.localId)) return;
        patchDraft(next.localId, {
          status: 'error',
          errorMessage: t('editProduct.modelPreparationError'),
        });
      })
      .finally(() => {
        convertingLockRef.current = false;
        setConvertTick((n) => n + 1);
      });
  }, [applyDraftToViewer, convertTick, drafts, options.enabled, patchDraft, t]);

  const waitForConversions = useCallback(async () => {
    const deadline = Date.now() + maxConversionWaitMs(draftsRef.current);
    while (Date.now() < deadline) {
      const busy = draftsRef.current.some(
        (d) => d.status === 'queued' || d.status === 'converting'
      );
      if (!busy) return;
      await new Promise((r) => window.setTimeout(r, 200));
    }
    const stillBusy = draftsRef.current.some(
      (d) => d.status === 'queued' || d.status === 'converting'
    );
    if (stillBusy) throw new Error('CONVERT_TIMEOUT');
  }, []);

  const waitForModelLoad = useCallback(async (mv: ModelViewerElement, expectedSrc: string) => {
    await waitForViewerPaint();
    const srcMatches = () => {
      const attr = mv.getAttribute('src') || '';
      const src = mv.src || '';
      return Boolean(expectedSrc) && (attr === expectedSrc || src === expectedSrc);
    };

    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('MODEL_LOAD_TIMEOUT')), 20000);
      const onLoad = () => {
        if (!srcMatches()) return;
        window.clearTimeout(timer);
        mv.removeEventListener('load', onLoad);
        resolve();
      };
      mv.addEventListener('load', onLoad);
    });
  }, []);

  const ensureDraftPreview = useCallback(
    async (draft: ProductDraft): Promise<ProductDraft> => {
      if (draftPreviewUri(draft)) return draft;
      const mv = options.modelViewerRef.current;
      if (!mv || !draft.modelUrl) throw new Error('PREVIEW_REQUIRED');

      const srcBefore = mv.src || mv.getAttribute('src') || '';
      applyDraftToViewer(draft);
      setActiveDraftId(draft.localId);
      await new Promise((r) => window.setTimeout(r, 50));

      const srcAfter = mv.src || mv.getAttribute('src') || '';
      const alreadyShowingThisModel = srcBefore === draft.modelUrl && srcAfter === draft.modelUrl;
      if (!alreadyShowingThisModel) {
        await waitForModelLoad(mv, draft.modelUrl);
      }

      await waitForViewerPaint();
      const attr = mv.getAttribute('src') || '';
      const src = mv.src || '';
      if (attr !== draft.modelUrl && src !== draft.modelUrl) {
        throw new Error('PREVIEW_REQUIRED');
      }
      const capture = await capturePreviewFromViewer(mv);
      const updated: ProductDraft = {
        ...draft,
        previewUri: capture.url,
        previewSourceModelUrl: draft.modelUrl,
        cameraOrbit: capture.cameraOrbit,
        cameraTarget: capture.cameraTarget,
        cameraFov: capture.cameraFieldOfView,
      };
      patchDraft(draft.localId, {
        previewUri: capture.url,
        previewSourceModelUrl: draft.modelUrl,
        cameraOrbit: capture.cameraOrbit,
        cameraTarget: capture.cameraTarget,
        cameraFov: capture.cameraFieldOfView,
      });
      onPreviewUriChangeRef.current(capture.url);
      onCameraChangeRef.current(
        capture.cameraOrbit,
        capture.cameraTarget,
        capture.cameraFieldOfView
      );
      return updated;
    },
    [applyDraftToViewer, options.modelViewerRef, patchDraft, waitForModelLoad]
  );

  const validateBeforeSubmit = useCallback((): string | null => {
    const pending = draftsRef.current.filter(isPendingCreateDraft);
    if (pending.length === 0) return t('editProduct.batch.nothingToCreate');

    for (const draft of pending) {
      const message = draftValidationMessage(draft, t);
      if (message) {
        selectDraft(draft.localId);
        return t('editProduct.batch.incompleteProduct', {
          name: draft.productName || draft.file.name,
        });
      }
    }
    return null;
  }, [selectDraft, t]);

  const submitAll = useCallback(async () => {
    if (isSubmitting) return;

    try {
      await waitForConversions();
    } catch {
      notifications.show({
        title: t('editProduct.system'),
        message: t('editProduct.batch.convertTimeout'),
        color: 'red',
      });
      return;
    }

    // Promote create-errors back to ready before validation/submit (model already converted).
    for (const draft of draftsRef.current) {
      if (isRetryableErrorDraft(draft)) {
        patchDraft(draft.localId, { status: 'ready', errorMessage: undefined });
      }
    }

    const validationError = validateBeforeSubmit();
    if (validationError) {
      notifications.show({
        title: t('editProduct.system'),
        message: validationError,
        color: 'red',
      });
      return;
    }

    const pending = draftsRef.current.filter(isPendingCreateDraft);
    if (pending.length === 0) {
      notifications.show({
        title: t('editProduct.system'),
        message: t('editProduct.batch.nothingToCreate'),
        color: 'red',
      });
      return;
    }

    submitCancelledRef.current = false;
    setIsSubmitting(true);
    setSubmitProgress({ current: 0, total: pending.length });

    const created: BatchCreateResult['created'] = [];
    const failed: BatchCreateResult['failed'] = [];

    for (let i = 0; i < pending.length; i += 1) {
      if (submitCancelledRef.current) break;

      let draft = draftsRef.current.find((d) => d.localId === pending[i].localId) ?? pending[i];
      // Skip only terminal non-retryable states (created, or conversion error without model).
      if (draft.status === 'created' || (draft.status === 'error' && !draft.modelUrl)) continue;

      patchDraft(draft.localId, { status: 'creating', errorMessage: undefined });
      setSubmitProgress({ current: i + 1, total: pending.length });

      try {
        if (draft.status === 'queued' || !draft.modelUrl) {
          throw new Error('MODEL_REQUIRED');
        }
        draft = await ensureDraftPreview(draft);
        if (submitCancelledRef.current) {
          patchDraft(draft.localId, { status: 'ready', errorMessage: undefined });
          break;
        }
        const isPublic = draft.isPublic;
        const linkShareMode: LinkShareMode = isPublic ? 'open' : draft.linkShareMode;
        const saved = await createProductFromParts({
          product: {
            productName: draft.productName,
            productPage: draft.productPage,
            articleNumber: draft.articleNumber,
            isPublic,
            descriptionHtml: draft.descriptionHtml,
            metadata: draft.metadata ?? metadataTableFromTags(draft.tags),
            category: draft.category,
            linkShareMode,
            linkSharePassword: draft.linkSharePassword,
          },
          modelFile: draft.file,
          modelPreviewUri: draft.previewUri,
          nonGltfModel: draft.nonGltfModel,
          preparedGlb: draft.preparedGlb,
          initialCameraOrbit: draft.cameraOrbit,
          initialCameraTarget: draft.cameraTarget,
          initialCameraFieldOfView: draft.cameraFov,
          annotations: draft.annotations,
        });

        patchDraft(draft.localId, {
          status: 'created',
          createdProductId: saved._id,
        });
        if (saved._id) {
          created.push({ productId: saved._id, productName: draft.productName });
        }
      } catch (error: unknown) {
        console.error('Batch product create failed:', error);
        const code = error instanceof Error ? error.message : '';
        const mapped =
          code === 'PREVIEW_REQUIRED'
            ? t('editProduct.uploadModelAndPreview')
            : code === 'FILE_TOO_LARGE'
              ? t('editProduct.fileSizeError')
              : code === 'MODEL_PREPARE_FAILED' || code === 'MODEL_REQUIRED'
                ? t('editProduct.modelPreparationError')
                : getUserFriendlyErrorMessage(error);
        patchDraft(draft.localId, { status: 'error', errorMessage: mapped });
        failed.push({ productName: draft.productName, error: mapped });
      }
    }

    setUsedSlots((prev) => (prev == null ? prev : prev + created.length));
    setIsSubmitting(false);
    setSubmitProgress(null);

    if (submitCancelledRef.current) {
      for (const draft of draftsRef.current) {
        if (draft.status === 'creating') {
          patchDraft(draft.localId, { status: 'ready', errorMessage: undefined });
        }
      }
    }

    setResult({ created, failed });
  }, [ensureDraftPreview, isSubmitting, patchDraft, t, validateBeforeSubmit, waitForConversions]);

  const cancelSubmit = useCallback(() => {
    submitCancelledRef.current = true;
  }, []);

  const dismissResult = useCallback(() => {
    const leftover: ProductDraft[] = [];
    for (const draft of draftsRef.current) {
      if (draft.status === 'created') {
        releaseDraftResources(draft);
      } else if (isRetryableErrorDraft(draft)) {
        // Continue editing: create failures become pending again without re-drop.
        leftover.push({ ...draft, status: 'ready', errorMessage: undefined });
      } else {
        leftover.push(draft);
      }
    }
    draftsRef.current = leftover;
    setDrafts(leftover);
    setResult(null);
    if (leftover.length === 0) {
      setActiveDraftId(null);
      applyPreparedModelRef.current(null, '', null);
      onPreviewUriChangeRef.current('');
      onCameraChangeRef.current('', '', '');
      onRestoreAnnotationsRef.current([]);
      return;
    }
    const next = leftover.find((d) => d.localId === activeDraftIdRef.current) ?? leftover[0];
    setActiveDraftId(next.localId);
    applyDraftToViewer(next);
  }, [applyDraftToViewer]);

  const reset = useCallback(() => {
    submitCancelledRef.current = true;
    for (const draft of draftsRef.current) {
      releaseDraftResources(draft);
    }
    draftsRef.current = [];
    setDrafts([]);
    setActiveDraftId(null);
    setResult(null);
    setSubmitProgress(null);
    setIsSubmitting(false);
    applyPreparedModelRef.current(null, '', null);
    onPreviewUriChangeRef.current('');
    onCameraChangeRef.current('', '', '');
    onRestoreAnnotationsRef.current([]);
  }, []);

  const activeDraft = useMemo(
    () => drafts.find((d) => d.localId === activeDraftId) ?? null,
    [drafts, activeDraftId]
  );

  const activeIndex = useMemo(
    () => drafts.findIndex((d) => d.localId === activeDraftId),
    [drafts, activeDraftId]
  );

  const isLastDraft = drafts.length > 0 && activeIndex === drafts.length - 1;

  const pendingDrafts = useMemo(() => drafts.filter(isPendingCreateDraft), [drafts]);

  const pendingDraftCount = pendingDrafts.length;

  const allDetailsComplete = pendingDraftCount > 0 && pendingDrafts.every(isDraftDetailsComplete);

  const goToAdjacentDraft = useCallback(
    (direction: -1 | 1, requireCurrentComplete: boolean) => {
      const index = draftsRef.current.findIndex((d) => d.localId === activeDraftIdRef.current);
      if (index < 0) return false;

      if (requireCurrentComplete) {
        const current = draftsRef.current[index];
        const message = draftValidationMessage(current, t);
        if (message) {
          notifications.show({
            title: t('editProduct.system'),
            message: t('editProduct.batch.fillCurrentFirst'),
            color: 'yellow',
          });
          return false;
        }
      }

      const next = draftsRef.current[index + direction];
      if (!next) return false;
      selectDraft(next.localId);
      return true;
    },
    [selectDraft, t]
  );

  const goNext = useCallback(() => goToAdjacentDraft(1, true), [goToAdjacentDraft]);
  const goPrev = useCallback(() => goToAdjacentDraft(-1, false), [goToAdjacentDraft]);

  const convertingCount = drafts.filter(
    (d) => d.status === 'queued' || d.status === 'converting'
  ).length;

  return {
    drafts,
    activeDraft,
    activeDraftId,
    activeIndex,
    isLastDraft,
    allDetailsComplete,
    isBatchMode,
    remainingSlots,
    productSlots,
    usedSlots,
    isSubmitting,
    submitProgress,
    result,
    setResult,
    convertingCount,
    pendingDraftCount,
    addFiles,
    removeDraft,
    selectDraft,
    updateDraftFields,
    syncFormValues,
    patchActiveDraft,
    commitOwnedPatch,
    updateActivePreview,
    goNext,
    goPrev,
    submitAll,
    cancelSubmit,
    retryFailedDrafts,
    dismissResult,
    reset,
  };
}
